import type {
  Alignment,
  ArrowShape,
  LoginSocialBarData,
  LoginSocialBarProperties,
  StyleProperties,
} from '../LoginSocialBar.types';
import { Layout } from '../LoginSocialBar.types';
import { defaultCompData, defaultCompProps } from '../constants';
import { parseBoolean } from './helpers';

type GetLoginBarStyleValuesOptions = {
  compData: LoginSocialBarData;
  compProps: LoginSocialBarProperties;
  styleProperties: StyleProperties;
};

const getDisplayModes = (
  compData: LoginSocialBarData,
  styleProperties: StyleProperties,
) => {
  const splitAndDeprecateMemberInitials =
    compData.loggedInMember === Layout.MemberInitials;

  const fallbackLoggedInDisplayMode = splitAndDeprecateMemberInitials
    ? Layout.AvatarOnly
    : compData.loggedInMember ?? defaultCompData.loggedInMember;

  const loggedInMember =
    styleProperties.loggedInMember ?? fallbackLoggedInDisplayMode;

  const fallbackLoggedOutDisplayMode =
    (styleProperties.loggedInMember ?? compData.loggedInMember) ===
    Layout.TextOnly
      ? Layout.TextOnly
      : defaultCompData.loggedOutMember;

  const loggedOutMember =
    styleProperties.loggedOutMember ??
    compData.loggedOutMember ??
    fallbackLoggedOutDisplayMode;

  return {
    loggedInMember,
    loggedOutMember,
  };
};

const getArrowStyles = (
  compProps: LoginSocialBarProperties,
  styleProperties: StyleProperties,
) => {
  const arrowShape = (styleProperties.arrowShape ??
    compProps.arrowShape ??
    defaultCompProps.arrowShape) as ArrowShape;

  const arrowSize =
    styleProperties.arrowSize ??
    compProps.arrowSize ??
    defaultCompProps.arrowSize;

  return {
    arrowShape,
    arrowSize: parseInt(arrowSize, 10),
  };
};

const getAvatarStyles = (
  compProps: LoginSocialBarProperties,
  styleProperties: StyleProperties,
) => {
  const iconSize =
    styleProperties.iconSize ?? compProps.iconSize ?? defaultCompProps.iconSize;

  const loggedOutIconSize =
    styleProperties.loggedOutIconSize ??
    compProps.loggedOutIconSize ??
    iconSize;

  return {
    iconSize: parseInt(iconSize, 10),
    loggedOutIconSize: parseInt(loggedOutIconSize, 10),
  };
};

const getShowLoggedInText = (
  compData: LoginSocialBarData,
  styleProperties: StyleProperties,
) => {
  const showLoggedInText =
    styleProperties.showLoggedInText ??
    compData.showLoggedInText ??
    defaultCompData.showLoggedInText;

  return parseBoolean(showLoggedInText);
};

const getShowBellIcon = (
  compProps: LoginSocialBarProperties,
  styleProperties: StyleProperties,
) => {
  const showBellIcon =
    styleProperties.showBellIcon ??
    compProps.showBellIcon ??
    defaultCompProps.showBellIcon;

  return parseBoolean(showBellIcon);
};

const getAlignments = (
  compProps: LoginSocialBarProperties,
  styleProperties: StyleProperties,
) => {
  const buttonsAlignment = (styleProperties.buttonsAlignment ??
    compProps.buttonsAlignment ??
    defaultCompProps.buttonsAlignment) as Alignment;

  const dropDownTextAlignment = (styleProperties.dropDownTextAlignment ??
    compProps.dropDownTextAlignment ??
    defaultCompProps.dropDownTextAlignment) as Alignment;

  return {
    buttonsAlignment,
    dropDownTextAlignment,
  };
};

export const getLoginBarStyleValues = ({
  compData,
  compProps,
  styleProperties,
}: GetLoginBarStyleValuesOptions) => {
  const displayModes = getDisplayModes(compData, styleProperties);
  const arrowStyles = getArrowStyles(compProps, styleProperties);
  const avatarStyles = getAvatarStyles(compProps, styleProperties);
  const showLoggedInText = getShowLoggedInText(compData, styleProperties);
  const showBellIcon = getShowBellIcon(compProps, styleProperties);
  const alignments = getAlignments(compProps, styleProperties);

  return {
    ...displayModes,
    ...avatarStyles,
    ...arrowStyles,
    ...alignments,
    showLoggedInText,
    showBellIcon,
  };
};
